/**
 * Canonical ShoppingCategory enum + descriptions.
 *
 * Lives in its own file so that both `packages/common/types/index.ts` and
 * `packages/common/types/googleTaxonomy.ts` can import it without triggering
 * a circular-import TDZ error. Re-exported from `index.ts` for consumers.
 *
 * Categories map 1:1 to Google taxonomy roots where possible — see
 * `googleTaxonomy.ts` for the full mapping. `Other` is reserved for genuinely
 * ambiguous traffic (industrial supplies, religious/ceremonial, mature) and
 * should be the exception, not the catch-all.
 *
 * DUP-614 expanded the enum from 8 → 15 entries (added Pets, BabyKids, Auto,
 * Food, Hardware, Office, Media) so the ranking signal isn't lost on the
 * ~11 Google taxonomy roots that previously collapsed into Other.
 */
export enum ShoppingCategory {
  All = 'all',
  Fashion = 'Fashion',
  Furniture = 'Furniture',
  HomeDecor = 'Home Decor',
  Electronics = 'Electronics',
  Beauty = 'Beauty',
  Toys = 'Toys',
  Sporting = 'Sporting',
  Pets = 'Pets',
  BabyKids = 'Baby & Kids',
  Auto = 'Auto',
  Food = 'Food',
  Hardware = 'Hardware',
  Office = 'Office',
  Media = 'Media',
  Other = 'Other',
}

/**
 * Categories that carry the commission: Furniture is ~90%+ of revenue and Home
 * Decor shares the same Wayfair-family partner base.
 *
 * This set is what "revenue-protected" means repo-wide. Today it decides which
 * turns get relevance-first (masonry) treatment and which are held on the
 * partner-skewed layout: `resolveRelevanceDefaultSort` EXCLUDES these
 * (`apps/api/src/lib/dupes/relevanceDefaultSort.ts`), and the `railCategory`
 * marker the chat client reads INCLUDES exactly these
 * (`apps/web/src/components/chat/ProductRow.tsx`).
 *
 * The two came from a matched pair of experiments, deliberately given the same
 * eligibility set so they could never double-treat a request:
 * `non-furniture-relevance-sort` (379352), which concluded WON and whose winner
 * now serves every eligible request, and `sponsored-picks-rail-v2` (379446),
 * which concluded LOST and was deleted on 2026-09-16 — see
 * `docs/features/active/sponsored-picks-rail/decisions.md` §7. Both are over;
 * the shared boundary is what survived them.
 *
 * Canonical here so client and server can never drift on which categories are
 * revenue-protected.
 */
export const REVENUE_PROTECTED_CATEGORIES: ReadonlySet<ShoppingCategory> =
  new Set([ShoppingCategory.Furniture, ShoppingCategory.HomeDecor])

const SHOPPING_CATEGORY_VALUES: ReadonlySet<string> = new Set(
  Object.values(ShoppingCategory)
)

/**
 * Type guard for a known `ShoppingCategory` value. Sources like
 * `product_categories.detected_category` are plain text, so callers that gate
 * behaviour on category (e.g. non-furniture masonry eligibility, shipped from
 * experiment 379352) must reject unknown strings here rather than letting them
 * fall through the `REVENUE_PROTECTED_CATEGORIES` check and read as eligible.
 */
export function isShoppingCategory(
  value: string | null | undefined
): value is ShoppingCategory {
  return value != null && SHOPPING_CATEGORY_VALUES.has(value)
}

export const ShoppingCategoryDescriptions: Record<ShoppingCategory, string> = {
  [ShoppingCategory.All]: 'All categories',
  [ShoppingCategory.Fashion]:
    'Clothing, footwear, jewelry, eyewear, handbags, and accessories worn on the body (e.g. shirts, shoes, rings, sunglasses, purses). DOES NOT include cosmetics (those are Beauty), device accessories (phone cases are Electronics), or watches that are primarily smart devices (Electronics).',
  [ShoppingCategory.Furniture]:
    'Real, physical furniture: sofas, chairs, tables, beds, desks, cribs, dressers, bookshelves. DOES NOT include furniture accessories like seat cushions, neck pillows, chair mats, or slipcovers — those are Home Decor if decorative, Other otherwise.',
  [ShoppingCategory.HomeDecor]:
    'Decorative or soft-goods home items: art prints, candles, mirrors, vases, rugs, bedding, throw pillows, curtains, kitchenware, dinnerware. DOES NOT include functional appliances (those are Electronics if powered, Other otherwise), hardware/tools, or cleaning supplies.',
  [ShoppingCategory.Electronics]:
    'Powered devices and their accessories: phones, laptops, tablets, gaming consoles, cameras, headphones, smartwatches, phone cases, chargers, cables, monitors, PC components (RAM, SSDs, GPUs), smart home devices, 3D printers, appliances like toasters/fans/air purifiers, and water filtration systems.',
  [ShoppingCategory.Beauty]:
    'Cosmetics, skincare, haircare, fragrance, and personal grooming products: foundation, setting powder, blush, lipstick, serums, moisturizer, cleansers, shampoo, hair tools (dryers, curling irons, straighteners), perfume, nail polish, electric razors, shavers, trimmers.',
  [ShoppingCategory.Toys]:
    "Toys, games, puzzles, fidgets, stress balls, squishies, plushies, action figures, board games, children's playsets, and collectibles designed for play. DOES NOT include video games or game software — those are Media.",
  [ShoppingCategory.Sporting]:
    'Athletic and outdoor equipment: bikes and e-bikes, treadmills, yoga mats, dumbbells, camping gear, ski equipment, fishing rods, golf clubs, sports balls. Activewear clothing is Fashion, not Sporting.',
  [ShoppingCategory.Pets]:
    'Pet food, supplies, accessories, and care: dog/cat food, treats, litter, leashes, collars, pet beds, carriers, pet toys, aquarium and reptile supplies, pet grooming. DOES NOT include veterinary medicines or service-animal certification gear.',
  [ShoppingCategory.BabyKids]:
    "Baby and child gear, clothing, and consumables: strollers, car seats, baby/kids clothing and shoes, diapers, bottles, school supplies, kids' backpacks. Cribs and changing tables are Furniture; play toys are Toys; powered baby devices (baby monitors, smart cameras, electric warmers) are Electronics.",
  [ShoppingCategory.Auto]:
    'Automotive parts, accessories, tools, and care products: car parts, tires, batteries, motor oil, dashcams, jump starters, car detailing supplies, roof racks, floor mats. DOES NOT include vehicles themselves, bicycles (Sporting), or motorcycle gear if primarily protective apparel (Fashion).',
  [ShoppingCategory.Food]:
    'Food, beverages, snacks, specialty groceries, meal kits, alcohol, coffee/tea, tobacco/vape, and consumables. DOES NOT include kitchenware/dinnerware (Home Decor), dietary supplements or vitamins (Beauty), or pet food (Pets).',
  [ShoppingCategory.Hardware]:
    'Hardware, tools, building materials, plumbing, electrical fixtures: power tools, hand tools, fasteners, lumber, paint, pipes, electrical outlets, recessed lighting fixtures (the fixture itself, not the decorative lamp). DOES NOT include decorative lighting / lamps (Home Decor), appliances (Electronics), or kitchenware (Home Decor).',
  [ShoppingCategory.Office]:
    'Office supplies and stationery: paper, pens, notebooks, printer ink and toner, binders, calendars, planners, desk organizers, label makers. DOES NOT include office furniture like desks or chairs (Furniture) or office electronics like laptops or monitors (Electronics).',
  [ShoppingCategory.Media]:
    'Books, magazines, music, movies, video games, software, audiobooks, e-books, digital subscriptions, and downloadable content. DOES NOT include physical media playback devices (Electronics) or board / tabletop games (Toys).',
  [ShoppingCategory.Other]:
    "Reserved catch-all for genuinely ambiguous or low-volume traffic: industrial / business supplies (B2B equipment), religious / ceremonial items, mature / adult-only products, and queries that can't be confidently bucketed. Most shopping queries should land in one of the named categories above — use Other only when no concrete category fits.",
}
